// admin/src/components/QuestionCardPro.jsx
import React from 'react';

const PHASE_INFO = {
  1: { label: 'Phase 1: Mindset', color: '#8b5cf6', icon: '🧠' },
  2: { label: 'Phase 2: Bucket Discovery', color: '#3b82f6', icon: '🎯' },
  3: { label: 'Phase 3: Area Refinement', color: '#10b981', icon: '🔍' }
};

const BUCKET_LABELS = {
  build_make: '🔨 Build & Make',
  understand_discover: '🔬 Understand & Discover',
  express_create: '🎨 Express & Create',
  help_connect: '❤️ Help & Connect',
  lead_organize: '📊 Lead & Organize',
  explore_experience: '🌍 Explore & Experience',
  protect_sustain: '🛡️ Protect & Sustain',
  think_compute: '💻 Think & Compute'
};

export default function QuestionCardPro({ question, onEdit, onDelete }) {
  const phaseInfo = PHASE_INFO[question.phase] || PHASE_INFO[1];
  
  const formatTag = (slug) => {
    if (!slug) return '';
    return slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Collect all unique tags from options
  const getAllTags = () => {
    const tags = new Set();
    (question.options || []).forEach(opt => {
      (opt.tags || []).forEach(t => tags.add(t));
      (opt.areaOfStudyTags || []).forEach(t => tags.add(t));
      if (opt.mindsetTag) tags.add(opt.mindsetTag);
    });
    return Array.from(tags);
  };

  const allTags = getAllTags();

  return (
    <article className="adm-card">
      <div className="adm-card__header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div className="adm-title" style={{ marginBottom: 8 }}>
              {question.text || 'Untitled Question'}
            </div>
            <div className="adm-badgerow">
              <span 
                className="adm-badge"
                style={{
                  background: phaseInfo.color,
                  color: 'white',
                  fontWeight: 700
                }}
              >
                {phaseInfo.icon} {phaseInfo.label}
              </span>
              <span className={`adm-badge ${question.enabled ? 'adm-badge--success' : 'adm-badge--muted'}`}>
                {question.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <span className="adm-badge adm-badge--neutral">
                {question.type === 'multiple' ? '☑️ Multi-select' : '⭕ Single-select'}
              </span>
            </div>
          </div>
        </div>

        {/* Bucket Assignment (for Phase 3 questions) */}
        {question.phase === 3 && question.bucket && (
          <div style={{
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '2px solid #86efac',
            borderRadius: 8,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#166534',
            marginBottom: 8
          }}>
            🎯 Targets: {BUCKET_LABELS[question.bucket] || formatTag(question.bucket)}
          </div>
        )}

        {/* Order & ID */}
        <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>
          Order: {question.order ?? 'N/A'} • ID: {question._id?.slice(-6) || '—'}
        </div>
      </div>

      <div className="adm-card__body">
        {/* Options */}
        <div className="adm-section">
          <div className="adm-section__title">
            📝 Options ({question.options?.length || 0})
          </div>
          {question.options && question.options.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {question.options.map((opt, idx) => (
                <div 
                  key={idx}
                  style={{
                    padding: '10px 12px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: '0.875rem'
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                    {idx + 1}. {opt.text}
                  </div>
                  {/* Show option-level tags */}
                  {(opt.tags?.length > 0 || opt.areaOfStudyTags?.length > 0 || opt.mindsetTag || opt.bucket) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {opt.tags?.map((tag, i) => (
                        <span 
                          key={i}
                          className="adm-badge adm-badge--neutral"
                          style={{ fontSize: '0.7rem' }}
                        >
                          {formatTag(tag)}
                        </span>
                      ))}
                      {opt.areaOfStudyTags?.map((tag, i) => (
                        <span 
                          key={i}
                          className="adm-badge"
                          style={{ 
                            background: '#e0f2fe',
                            color: '#075985',
                            fontSize: '0.7rem'
                          }}
                        >
                          📚 {formatTag(tag)}
                        </span>
                      ))}
                      {opt.mindsetTag && (
                        <span 
                          className="adm-badge"
                          style={{ 
                            background: '#f3e8ff',
                            color: '#6b21a8',
                            fontSize: '0.7rem'
                          }}
                        >
                          🧠 {formatTag(opt.mindsetTag)}
                        </span>
                      )}
                      {opt.bucket && (
                        <span 
                          className="adm-badge"
                          style={{ 
                            background: '#dcfce7',
                            color: '#166534',
                            fontSize: '0.7rem'
                          }}
                        >
                          🎯 {BUCKET_LABELS[opt.bucket] || formatTag(opt.bucket)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span className="adm-empty">No options defined</span>
          )}
        </div>

        {/* All Tags Summary */}
        {allTags.length > 0 && (
          <div className="adm-section">
            <div className="adm-section__title">
              🏷️ Tags Used ({allTags.length})
            </div>
            <div className="adm-pillrow">
              {allTags.slice(0, 8).map((tag, idx) => (
                <span key={idx} className="adm-tag">
                  {formatTag(tag)}
                </span>
              ))}
              {allTags.length > 8 && (
                <span className="adm-more">+{allTags.length - 8} more</span>
              )}
            </div>
          </div>
        )}

        {/* Warning if no options */}
        {(!question.options || question.options.length === 0) && (
          <div style={{
            padding: '8px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            fontSize: '0.8rem',
            color: '#dc2626',
            marginTop: 8
          }}>
            ⚠️ No options defined - question won't work properly
          </div>
        )}

        <div className="adm-actions">
          <button className="btn btn--ghost" onClick={onEdit}>Edit</button>
          <button className="btn btn--danger" onClick={onDelete}>Delete</button>
        </div>
      </div>
    </article>
  );
}